import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService, Appointment, Provider, Procedure, Operatory } from '../../services/apiService';
import moment from 'moment';

interface AppointmentFormProps {
  appointment?: Appointment;
  onClose: () => void;
  onSave: () => void;
  selectedDate?: Date;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({ 
  appointment, 
  onClose, 
  onSave, 
  selectedDate 
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [operatories, setOperatories] = useState<Operatory[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    patient_phone: '',
    provider_id: '',
    procedure_id: '',
    operatory_id: '',
    appointment_date: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    start_time: '09:00',
    end_time: '10:00',
    notes: '',
    language: 'en',
    status: 'scheduled' as Appointment['status']
  });

  useEffect(() => {
    loadData();
    
    // If editing existing appointment, populate form
    if (appointment) {
      setFormData({
        patient_name: appointment.patient_name,
        patient_email: appointment.patient_email,
        patient_phone: appointment.patient_phone,
        provider_id: appointment.provider_id.toString(),
        procedure_id: appointment.procedure_id.toString(),
        operatory_id: appointment.operatory_id.toString(),
        appointment_date: appointment.appointment_date,
        start_time: appointment.start_time,
        end_time: appointment.end_time,
        notes: appointment.notes || '',
        language: appointment.language,
        status: appointment.status
      });
    }
  }, [appointment]);

  const loadData = async () => {
    try {
      const [providersRes, proceduresRes, operatoriesRes] = await Promise.all([
        apiService.getProviders(),
        apiService.getProcedures(),
        apiService.getOperatories()
      ]);

      setProviders(providersRes.data.data || []);
      setProcedures(proceduresRes.data.data || []);
      setOperatories(operatoriesRes.data.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(t('common.error'));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProcedureChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const procedureId = e.target.value;
    setFormData(prev => ({
      ...prev,
      procedure_id: procedureId
    }));

    // Auto-calculate end time based on procedure duration
    if (procedureId) {
      const procedure = procedures.find(p => p.id === parseInt(procedureId));
      if (procedure) {
        const startMoment = moment(formData.start_time, 'HH:mm');
        const endMoment = startMoment.clone().add(procedure.duration, 'minutes');
        setFormData(prev => ({
          ...prev,
          end_time: endMoment.format('HH:mm')
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.patient_name || !formData.patient_email || !formData.provider_id || 
          !formData.procedure_id || !formData.operatory_id) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Check for conflicts
      const conflictCheck = await apiService.checkConflicts({
        date: formData.appointment_date,
        startTime: formData.start_time,
        endTime: formData.end_time,
        providerId: parseInt(formData.provider_id),
        operatoryId: parseInt(formData.operatory_id),
        excludeId: appointment?.id
      });

      if (conflictCheck.data.data && Array.isArray(conflictCheck.data.data) && conflictCheck.data.data.length > 0) {
        setError(t('messages.conflictDetected'));
        setLoading(false);
        return;
      }

      const appointmentData = {
        ...formData,
        provider_id: parseInt(formData.provider_id),
        procedure_id: parseInt(formData.procedure_id),
        operatory_id: parseInt(formData.operatory_id)
      };

      if (appointment && appointment.id) {
        // Update existing appointment
        await apiService.updateAppointment(appointment.id, appointmentData);
      } else {
        // Create new appointment
        await apiService.createAppointment(appointmentData);
      }

      onSave();
    } catch (err: any) {
      console.error('Error saving appointment:', err);
      const backendMsg = err?.response?.data?.error || err?.response?.data?.message;
      setError(backendMsg || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="w-full">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {appointment ? t('appointments.update') : t('appointments.create')}
                    </h3>
                    <button
                      type="button"
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {error && (
                    <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  {/* Form Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Patient Information */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
                        {t('appointments.patient')} *
                      </h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('appointments.patient')} *
                        </label>
                        <input
                          type="text"
                          name="patient_name"
                          value={formData.patient_name}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                          placeholder="Max Mustermann"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="patient_email"
                          value={formData.patient_email}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                          placeholder="max@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('providers.phone')} *
                        </label>
                        <input
                          type="tel"
                          name="patient_phone"
                          value={formData.patient_phone}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                          placeholder="+34-612-345678"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('appointments.language')}
                        </label>
                        <select
                          name="language"
                          value={formData.language}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                        >
                          <option value="en">English</option>
                          <option value="de">Deutsch</option>
                          <option value="es">Español</option>
                          <option value="ru">Русский</option>
                        </select>
                      </div>
                    </div>

                    {/* Appointment Details */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
                        {t('appointments.title')} *
                      </h4>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('appointments.provider')} *
                        </label>
                        <select
                          name="provider_id"
                          value={formData.provider_id}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                        >
                          <option value="">Select Provider</option>
                          {providers.map((provider) => (
                            <option key={provider.id} value={provider.id}>
                              {provider.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('appointments.procedure')} *
                        </label>
                        <select
                          name="procedure_id"
                          value={formData.procedure_id}
                          onChange={handleProcedureChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                        >
                          <option value="">Select Procedure</option>
                          {procedures.map((procedure) => (
                            <option key={procedure.id} value={procedure.id}>
                              {typeof procedure.name === 'string' ? procedure.name : (procedure.name as any)?.de || ''} ({procedure.duration}min)
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('appointments.operatory')} *
                        </label>
                        <select
                          name="operatory_id"
                          value={formData.operatory_id}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                        >
                          <option value="">Select Operatory</option>
                          {operatories.map((operatory) => (
                            <option key={operatory.id} value={operatory.id}>
                              {operatory.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('appointments.date')} *
                        </label>
                        <input
                          type="date"
                          name="appointment_date"
                          value={formData.appointment_date}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Start Time *
                          </label>
                          <input
                            type="time"
                            name="start_time"
                            value={formData.start_time}
                            onChange={handleInputChange}
                            required
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            End Time *
                          </label>
                          <input
                            type="time"
                            name="end_time"
                            value={formData.end_time}
                            onChange={handleInputChange}
                            required
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                          />
                        </div>
                      </div>

                      {appointment && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            {t('appointments.status')}
                          </label>
                          <select
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                          >
                            <option value="scheduled">{t('status.scheduled')}</option>
                            <option value="confirmed">{t('status.confirmed')}</option>
                            <option value="completed">{t('status.completed')}</option>
                            <option value="cancelled">{t('status.cancelled')}</option>
                            <option value="no_show">{t('status.no_show')}</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('appointments.notes')}
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-navy-admin focus:border-navy-admin sm:text-sm"
                      placeholder="Additional notes..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-navy-admin text-base font-medium text-white hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-admin sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('common.loading')}
                  </div>
                ) : (
                  t('common.save')
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-admin sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppointmentForm; 